import { useContext, useState, createContext, useEffect } from "react";
import { SupplierDeal } from "../types";
import useSWR from "swr";
import apiClient from "../lib/apiClient";
import tracking from "../lib/tracking";

interface AppConfig {
  title: string;
  setTitle: (title: string) => void;
  link: string | null;
  setLink: (link: string) => void;
  bgColor: string;
  setBgColor: (bgColor: string) => void;
  color: string;
  setColor: (color: string) => void;
  kwh: number;
  setKwh: (kwh: number) => void;
  trackingId: string | null;
  trackingSecret: string | null;
  setTrackingId: (trackingId: string) => void;
  setTrackingSecret: (trackingSecret: string) => void;
  compareDeal: SupplierDeal | null;
  currentSupplier: number | null;
  setCurrentSupplier?: (value: number) => void;
  currentDeal: SupplierDeal | null;
  setCurrentDeal?: (deal: SupplierDeal) => void;
  cheapestDeal?: SupplierDeal | null;
  setCheapestDeal?: (deal: SupplierDeal) => void;
}

const AppConfigContext = createContext<AppConfig>({
  title: "",
  setTitle: () => {},
  link: "",
  setLink: () => {},
  bgColor: "",
  setBgColor: () => {},
  trackingId: null,
  trackingSecret: null,
  setTrackingId: () => {},
  setTrackingSecret: () => {},
  compareDeal: null,
  color: "",
  setColor: () => {},
  kwh: 2000,
  setKwh: () => {},
  currentSupplier: null,
  setCurrentSupplier: () => {},
  currentDeal: null,
  setCurrentDeal: () => {},
  cheapestDeal: null,
  setCheapestDeal: () => {},
});

interface initialPropsType {
  title?: string;
  bgColor?: string;
  color?: string;
  dealId?: number;
  compareDealId?: number | null;
  link?: string;
  kwh?: number;
  trackingId?: string;
  trackingSecret?: string;
}

const AppConfigProvider = ({
  children,
  initialProps,
}: {
  children: React.ReactNode;
  initialProps?: initialPropsType;
}) => {
  const [title, setTitle] = useState<string>(
    initialProps?.title || "[INDSÆT TITLE]",
  );
  const [bgColor, setBgColor] = useState<string>(
    initialProps?.bgColor || "#f5f5f5",
  );
  const [color, setColor] = useState<string>(initialProps?.color || "#0000ff");
  const [currentSupplier, setCurrentSupplier] = useState<number | null>(null);
  const [currentDeal, setCurrentDeal] = useState<SupplierDeal | null>(null);
  const [cheapestDeal, setCheapestDeal] = useState<SupplierDeal | null>(null);
  const [kwh, setKwh] = useState<number>(initialProps?.kwh || 2000);
  const [compareDeal, setCompareDeal] = useState<SupplierDeal | null>(null);
  const [link, setLink] = useState<string | null>(initialProps?.link || null);
  const [trackingId, setTrackingId] = useState<string | null>(
    initialProps?.trackingId || null,
  );
  const [trackingSecret, setTrackingSecret] = useState<string | null>(
    initialProps?.trackingSecret || null,
  );

  useEffect(() => {
    if (initialProps?.compareDealId) {
      apiClient()
        .getDeal(initialProps?.compareDealId)
        .then((response) => {
          setCompareDeal(response.data.data);
        });
    }
  }, [initialProps?.compareDealId]);

  const { data } = useSWR(
    "/deal",
    async () => {
      return await apiClient().getDeal(initialProps?.dealId || 0);
    },
    {
      revalidateOnFocus: false,
    },
  );

  useEffect(() => {
    if (!data) return;
    setCompareDeal(data.data.data);
  }, [data]);

  useEffect(() => {
    setCurrentDeal(null);
  }, [currentSupplier]);

  return (
    <AppConfigContext.Provider
      value={{
        title,
        setTitle,
        link,
        setLink,
        bgColor,
        setBgColor,
        color,
        trackingId,
        trackingSecret,
        setTrackingId,
        setTrackingSecret,
        compareDeal,
        setColor,
        kwh,
        setKwh,
        currentSupplier,
        setCurrentSupplier,
        currentDeal,
        setCurrentDeal,
        cheapestDeal,
        setCheapestDeal,
      }}
    >
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => {
  return useContext(AppConfigContext);
};

export default AppConfigProvider;
